import { singletonInstanceSummoner } from '../../../common/singletonInstanceSummoner'
import { registerFunctionLoggedInExecuteOnce } from '../../../common/utils'
import { ModelCoreUserMeta } from '../models/core_user_meta'

const MAX_KEY_LENGTH = 255

/**
 * # $core.$userMetaData
 * - ユーザのメタデータを保存, loadするサービス。
 * - データ保存先: Table上, `core_user_meta` へ key-value形式で保存されます。
 *
 * ## get
 * ```ts
 * // 取得
 * const theVal = await $core.$userMetaData.get('someKey')
 * ```
 *
 * ## set
 * - `save` は `set` のaliasです。
 * ```ts
 * // 保存
 * await $core.$userMetaData.set('someKey', {some: 'vvv'})
 * // または、
 * // await $core.$userMetaData.save('someKey', {some: 'vvv'})
 * ```
 *
 * ## bulkSet
 * ```ts
 * // 一括保存
 * await $core.$userMetaData.bulkSet({
 *   someKey: {some: 'vvv'},
 *   estimatedAge: 28,
 *   estimatedGender: 'female',
 * })
 * ```
 *
 * @summaryOnly
 * @coreDocPath $core/30.utils/302.userMetaData
 */
export class UserMetaDataService {
  private _metaData: Record<string, ModelCoreUserMeta['value']>
  get metaData() {
    return this._metaData
  }

  constructor() {
    this._metaData = {}
    // signIn時にデータロード
    registerFunctionLoggedInExecuteOnce({
      appHookFunctionRegisterKey: 'UserMetaDataService',
      onceLoadFunction: async (userData) => {
        try {
          await this.fetchAll()
        } catch (e) {
          console.error(e)
          // $core.$errorReporter.r(e, this)
        }
      }, // 初回ロード時に実行する関数を登録
      useAwaitOnLoad: true, // ロード時にawaitするかどうか ( => $core.$embAuth.user が初期化する前に実施するか、または、初期化後に実施で良い場合はfalse)
    })
  }

  static get instance(): UserMetaDataService {
    return singletonInstanceSummoner('UserMetaDataService', UserMetaDataService)
  }

  // TODO: asyncではないほうが良いとは思うが...?
  public get(keyName: string) {
    return this._metaData[keyName]
  }

  /**
   * 値を保存する
   * @param keyName
   * @param data
   */
  public async set(keyName: string, data: any): Promise<void> {
    if (!keyName || typeof keyName !== 'string') {
      console.warn(
        `[${this.constructor.name}] keyName should be string, given keyName: ${keyName}`,
      )
      return
    }
    return this._updateData(keyName, data)
  }

  public async remove(keyName: string) {
    const id = this.genId(keyName)
    await $core.$storeMethods.delete({
      modelName: 'core_user_meta',
      id,
      quiet: true,
    })
    delete this._metaData[keyName]
  }

  /**
   * 複数の値を一括保存する
   * @param saveData, key: value のオブジェクト
   */
  public async bulkSet(saveData: Record<string, any>): Promise<void> {
    if (
      !saveData ||
      typeof saveData !== 'object' ||
      Object.keys(saveData)?.length === 0
    ) {
      console.warn(
        `[${this.constructor.name}] saveData should be object, given: ${saveData}`,
      )
      return
    }
    return this._bulkUpdateData(saveData)
  }

  // alias
  public save(keyName: string, data: any) {
    return this.set(keyName, data)
  }
  /**
   * ユーザーデータから metaData フィールドを Loadする
   */
  private async fetchAll() {
    const userMetaDatas = await $core.$models.core_user_meta.find({
      filter: {
        user: {
          _eq: $core.$embAuth.user.id,
        },
      },
      limit: -1,
    })
    this._metaData = userMetaDatas.reduce((acc, cur) => {
      acc[cur.key] = cur.value
      return acc
    }, {})
  }

  private genId(keyName: string) {
    return `${$core.$embAuth.user.id}-${keyName}`
  }

  /**
   * データ更新
   * @param keyName
   * @param data
   */
  private async _updateData(keyName: string, data: any) {
    await this._bulkUpdateData({ [keyName]: data })
  }

  private async _bulkUpdateData(saveData: Record<string, any>) {
    try {
      const saveRecords = Object.entries(saveData).map(([key, value]) => {
        if (key.length > MAX_KEY_LENGTH) {
          const msg = `[${this.constructor.name}] keyName should be less than ${MAX_KEY_LENGTH} characters, given keyName: ${key}`
          $core.$toast.error(msg)
          throw new Error(msg)
        }
        return {
          id: this.genId(key),
          user: $core.$embAuth.user.id,
          key,
          value,
        }
      })
      await $core.$storeMethods.bulkUpsert({
        modelName: 'core_user_meta',
        data: saveRecords,
        quiet: true,
      })
      Object.entries(saveData).forEach(([key, value]) => {
        this._metaData[key] = value
      })
    } catch (e) {
      // Rollback
      // this._metaData = beforeData
      console.error(e)
      $core.$errorReporter.r(e, this)
    }
  }
}

export const $userMetaData = UserMetaDataService.instance
